import React from "react";
import { Components } from "react-markdown";
import {
  ChevronRight,
  CheckCircle2,
  Circle,
  Loader2,
} from "lucide-react";
import { getExpertPalette, getStepStyle } from "../utils/expertStyles";
import StreamingMarkdown from "./StreamingMarkdown";
import DebateTimeline, { TimelineStage } from "./DebateTimeline";
import { buildMultiStages, buildSingleStages, expertLabel } from "../utils/debateStages";
import CopyButton from "./CopyButton";
import VerdictMeter from "./VerdictMeter";
import { Verdict } from "../utils/verdict";
import { DebateSource, visibleSources } from "../utils/sources";
import SourcesDisclosure from "./SourcesList";

/** The model that most recently answered, since one expert can span several models (e.g. research gather → synthesize). */
function latestModelEntry(
  entries: Array<{ model?: string; fallbackUsed?: boolean }>,
): { model: string; fallbackUsed: boolean } | null {
  for (let i = entries.length - 1; i >= 0; i--) {
    const model = entries[i].model;
    if (model) return { model, fallbackUsed: entries[i].fallbackUsed === true };
  }
  return null;
}

function ModelTag({
  entry,
}: {
  entry: { model: string; fallbackUsed: boolean } | null;
}) {
  if (!entry) return null;
  return (
    <span
      className="max-w-[16rem] truncate font-mono text-xs font-normal text-muted"
      title={entry.fallbackUsed ? `${entry.model} (fallback after the assigned model failed)` : entry.model}
    >
      {entry.model}
      {entry.fallbackUsed ? " · fallback" : ""}
    </span>
  );
}

interface DebateProgressProps {
  debateTopic: string | null;
  debateImages?: string[];
  councilProgress: any[];
  chaosProgress: any[];
  councilFinal: string | null;
  facilitatorDone: boolean;
  isLoading: boolean;
  selectedExperts: string[];
  expandedExperts: Record<string, boolean>;
  onToggleExpert: (expert: string) => void;
  debateMode?: "single" | "multi";
  /** The panel verdict on a question with fixed answers. */
  verdict?: Verdict | null;
  /** Links the experts relied on. */
  sources?: DebateSource[];
}

export const markdownComponents: Components = {
  table: ({ node, ...props }) => (
    <div className="my-3 overflow-x-auto rounded-xl border border-line bg-subtle/60 shadow-sm">
      <table
        className="w-full min-w-[560px] border-collapse text-sm"
        {...props}
      />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead className="bg-pressed/80" {...props} />
  ),
  tbody: ({ node, ...props }) => <tbody className="bg-surface" {...props} />,
  tr: ({ node, ...props }) => (
    <tr
      className="border-b border-line last:border-b-0 odd:bg-surface even:bg-subtle/65 hover:bg-accent-soft/60 transition-colors"
      {...props}
    />
  ),
  th: ({ node, ...props }) => (
    <th
      className="border-b border-control px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-content"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      className="px-3 py-2.5 align-top text-label leading-relaxed text-content"
      {...props}
    />
  ),
};

/**
 * Renders the full debate progress view: topic banner, per-expert cards with
 * step pills and content blocks, chaos agent attacks, and facilitator synthesis.
 */
const DebateProgress: React.FC<DebateProgressProps> = ({
  debateTopic,
  debateImages = [],
  councilProgress,
  chaosProgress,
  councilFinal,
  facilitatorDone,
  isLoading,
  selectedExperts,
  expandedExperts,
  onToggleExpert,
  debateMode = "multi",
  verdict = null,
  sources = [],
}) => {
  // Rounds are always 1
  const rounds = 1;

  /** Build step descriptors for a given expert */
  const getExpertSteps = (expert: string) => {
    const progress = councilProgress.filter((step) => step.expert === expert);

    // If this expert has a research_gather step in progress data, prepend it
    const hasGatherStep = progress.some((p) => p.step === "research_gather");

    const steps: {
      key: string;
      label: string;
      done: boolean;
      started: boolean;
    }[] = [];

    if (hasGatherStep) {
      steps.push({
        key: "research_gather",
        label: "Gathering research",
        done: false,
        started: false,
      });
    }

    steps.push({
      key: "initial",
      label: "Initial",
      done: false,
      started: false,
    });

    for (let r = 1; r <= rounds; r++) {
      steps.push({
        key: `feedback-${r}`,
        label: `Feedback (R${r})`,
        done: false,
        started: false,
      });
      steps.push({
        key: `chaos-${r}`,
        label: `Chaos Attack (R${r})`,
        done: false,
        started: false,
      });
      steps.push({
        key: `revision-${r}`,
        label: `Revision (R${r})`,
        done: false,
        started: false,
      });
    }

    return steps.map((step) => {
      let done = false;
      let started = false;
      if (step.key === "research_gather") {
        started = progress.some((p) => p.step === "research_gather");
        done = progress.some(
          (p) => p.step === "research_gather" && p.done === true,
        );
      } else if (step.key === "initial") {
        started = progress.some((p) => p.step === "initial");
        done = progress.some((p) => p.step === "initial" && p.done === true);
      } else if (step.key.startsWith("chaos-")) {
        const round = Number(step.key.split("-")[1]);
        started = chaosProgress.some((p) => p.round === round);
        done = chaosProgress.some((p) => p.round === round && p.done === true);
      } else {
        const [type, roundStr] = step.key.split("-");
        const round = Number(roundStr);
        started = progress.some((p) => p.step === type && p.round === round);
        done = progress.some(
          (p) => p.step === type && p.round === round && p.done === true,
        );
      }
      return { ...step, done, started };
    });
  };

  const stageInput = { councilProgress, chaosProgress, councilFinal, facilitatorDone, isLoading, selectedExperts };
  const singleStages: TimelineStage[] = debateMode === "single" ? buildSingleStages(stageInput) : [];
  const multiStages: TimelineStage[] = debateMode === "multi" ? buildMultiStages(stageInput) : [];

  return (
    <div className="debate-layout">
      {debateMode === "multi" && (councilProgress.length > 0 || isLoading) && (
        <DebateTimeline stages={multiStages} isLoading={isLoading} />
      )}
      {debateMode === "single" && singleStages.length > 0 && (
        <DebateTimeline stages={singleStages} isLoading={isLoading} title="Analysis progress" />
      )}
    <div className="debate-layout-main space-y-4">
      {/* Debate topic banner */}
      {debateTopic && (
        <div className="border border-warning-line bg-warning-soft rounded-lg p-4" style={{ viewTransitionName: "debate-topic" } as React.CSSProperties}>
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-semibold text-warning">
                Debate topic
              </div>
              <div className="mt-1 text-sm whitespace-pre-wrap">
                {debateTopic}
              </div>
              {debateImages.length > 0 && (
                <div className="mt-2 flex gap-2">
                  {debateImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Debate attachment ${idx + 1}`}
                      className="max-h-40 max-w-full rounded-md border border-warning-line object-contain bg-surface"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Single expert linear timeline ──────────────────────────────── */}
      {debateMode === "single" && (
        <>
          {councilProgress.length === 0 && !isLoading && (
            <div className="text-muted text-center text-sm">
              Start an analysis to see results.
            </div>
          )}

          {/* Pipeline phases as vertical timeline */}
          {(() => {
            // Define the pipeline phases in order
            type Phase = {
              key: string;
              label: string;
              type: "step" | "chaos";
              expert?: string;
              step?: string;
              colors: {
                bg: string;
                border: string;
                text: string;
                headerBg: string;
              };
            };
            const phases: Phase[] = [
              {
                key: "frame",
                label: "Framing",
                type: "step",
                expert: "framer",
                step: "frame",
                colors: {
                  bg: "bg-accent-soft/30",
                  border: "border-accent-line",
                  text: "text-accent",
                  headerBg: "bg-accent-soft",
                },
              },
              {
                key: "research",
                label: "Research",
                type: "step",
                expert: "researcher",
                step: "research_gather",
                colors: {
                  bg: "bg-accent-soft/30",
                  border: "border-accent-line",
                  text: "text-accent",
                  headerBg: "bg-accent-soft",
                },
              },
              {
                key: "initial",
                label: "Expert Analysis",
                type: "step",
                expert: "analyst",
                step: "initial",
                colors: {
                  bg: "bg-accent-soft/30",
                  border: "border-accent-line",
                  text: "text-accent",
                  headerBg: "bg-accent-soft",
                },
              },
              {
                key: "chaos",
                label: "Chaos Challenge",
                type: "chaos",
                colors: {
                  bg: "bg-danger-soft/30",
                  border: "border-danger-line",
                  text: "text-danger",
                  headerBg: "bg-danger-soft",
                },
              },
              {
                key: "fact_check",
                label: "Fact Check",
                type: "step",
                expert: "fact_checker",
                step: "fact_check",
                colors: {
                  bg: "bg-success-soft/30",
                  border: "border-success-line",
                  text: "text-success",
                  headerBg: "bg-success-soft",
                },
              },
              {
                key: "revision",
                label: "Final Answer",
                type: "step",
                expert: "analyst",
                step: "revision",
                colors: {
                  bg: "bg-success-soft/30",
                  border: "border-success-line",
                  text: "text-success",
                  headerBg: "bg-success-soft",
                },
              },
            ];

            // Filter out research phase if it doesn't appear (free tier skips it)
            const hasResearch = councilProgress.some(
              (s) => s.step === "research_gather",
            );
            const hasFrame = councilProgress.some((s) => s.step === "frame");
            const activePipeline = phases.filter((p) => {
              if (p.key === "research" && !hasResearch) return false;
              if (p.key === "frame" && !hasFrame) return false;
              return true;
            });

            // Separate the revision (final answer) from the pipeline phases
            const intermediatePipeline = activePipeline.filter(
              (p) => p.key !== "revision",
            );
            const revisionPhase = activePipeline.find(
              (p) => p.key === "revision",
            );

            // Resolve revision content
            let revisionContent = "";
            let revisionDone = false;
            let revisionStarted = false;
            if (revisionPhase) {
              const entries = councilProgress.filter(
                (s) =>
                  s.expert === revisionPhase.expert &&
                  s.step === revisionPhase.step,
              );
              if (entries.length > 0) {
                const latest = entries[entries.length - 1];
                revisionContent =
                  latest.answer || latest.feedback || latest.revised || "";
                revisionDone = latest.done === true;
                revisionStarted = true;
              }
            }

            return (
              <div className="space-y-3">
                {/* ── Final Answer — always at the top ──────────── */}
                {revisionPhase && (
                  <div
                    className={`rounded-xl border-2 overflow-hidden ${
                      revisionDone
                        ? "border-success-line"
                        : revisionStarted
                          ? "border-success-line"
                          : "border-line"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-between px-4 py-3 ${
                        revisionDone ? "bg-success-soft" : "bg-subtle"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`text-sm font-semibold ${
                            revisionDone ? "text-success" : "text-muted"
                          }`}
                        >
                          Final Answer
                        </span>
                      </div>
                      {revisionDone ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-success bg-success-soft px-1.5 py-0.5 rounded-md border border-success-line">
                          <CheckCircle2 className="w-3 h-3" /> Complete
                        </span>
                      ) : revisionStarted && isLoading ? (
                        <div className="flex space-x-1 items-center text-xs text-success">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span className="ml-1 text-xs">writing…</span>
                        </div>
                      ) : isLoading ? (
                        <span className="text-xs font-medium text-muted">
                          Waiting on prior steps…
                        </span>
                      ) : null}
                    </div>
                    {revisionContent ? (
                      <div className="p-4 overflow-x-hidden bg-success-soft/30">
                        <StreamingMarkdown
                          text={revisionContent}
                          streaming={isLoading && !revisionDone}
                          components={markdownComponents}
                          className="prose prose-sm max-w-full break-words leading-relaxed"
                        />
                        {revisionDone && (
                          <div className="message-footer">
                            <span />
                            <CopyButton label="Copy answer" getText={() => revisionContent} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-sm text-muted italic bg-subtle/30">
                        {isLoading
                          ? "The expert is working, the final answer will appear here once all phases complete."
                          : "No final answer yet."}
                      </div>
                    )}
                  </div>
                )}

                {/* Intermediate phase cards — collapsible, collapsed by default */}
                {intermediatePipeline.map((phase) => {
                  let content = "";
                  let done = false;
                  let modelEntry: ReturnType<typeof latestModelEntry> = null;

                  if (phase.type === "chaos") {
                    const chaosEntry = chaosProgress.find((c) => c.round === 1);
                    if (!chaosEntry) return null;
                    content = chaosEntry.chaos || "";
                    done = chaosEntry.done === true;
                    modelEntry = latestModelEntry([chaosEntry]);
                  } else {
                    const entries = councilProgress.filter(
                      (s) => s.expert === phase.expert && s.step === phase.step,
                    );
                    if (entries.length === 0) return null;
                    const latest = entries[entries.length - 1];
                    content =
                      latest.answer || latest.feedback || latest.revised || "";
                    done = latest.done === true;
                    modelEntry = latestModelEntry(entries);
                  }

                  const isOpen =
                    expandedExperts[`single-${phase.key}`] === true;

                  return (
                    <div
                      key={phase.key}
                      className={`rounded-xl border overflow-hidden ${phase.colors.border}`}
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        className={`w-full flex items-center justify-between px-4 py-2.5 ${phase.colors.headerBg} transition-[filter] duration-150 hover:brightness-95 active:brightness-90 focus-visible:outline-offset-[-3px]`}
                        onClick={() => onToggleExpert(`single-${phase.key}`)}
                      >
                        <div className="flex items-center gap-1.5">
                          <ChevronRight
                            aria-hidden="true"
                            className={`disclosure-chevron w-3.5 h-3.5 ${phase.colors.text}`}
                          />
                          <span
                            className={`text-xs font-semibold ${phase.colors.text}`}
                          >
                            {phase.label}
                          </span>
                          <ModelTag entry={modelEntry} />
                        </div>
                        <div className="flex items-center gap-1.5">
                          {done ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-success bg-success-soft px-1.5 py-0.5 rounded-md border border-success-line">
                              <CheckCircle2 className="w-3 h-3" /> Done
                            </span>
                          ) : isLoading ? (
                            <div className="flex space-x-1 items-center text-xs text-accent">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span className="ml-1 text-xs">writing…</span>
                            </div>
                          ) : null}
                        </div>
                      </button>
                      {isOpen && (
                        <div
                          className={`motion-fade p-4 overflow-x-hidden ${phase.colors.bg}`}
                        >
                          <StreamingMarkdown
                            text={content}
                            streaming={isLoading && !done}
                            components={markdownComponents}
                            className="prose prose-sm max-w-full break-words leading-relaxed"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}

      {/* ── Multi-expert debate view ───────────────────────────────────── */}
      {debateMode === "multi" && (
        <>
          {/* Empty state */}
          {councilProgress.length === 0 && !isLoading && (
            <div className="text-muted text-center text-sm">
              Start a debate to see expert responses.
            </div>
          )}

          <div className="space-y-4" style={{ viewTransitionName: "debate-panel" } as React.CSSProperties}>
          {selectedExperts.map((expert) => {
            const steps = getExpertSteps(expert);
            const doneCount = steps.filter((s) => s.done).length;
            const totalCount = steps.length;
            const isOpen = expandedExperts[expert] === true;
            const palette = getExpertPalette(expert, selectedExperts);
            const allDone = doneCount === totalCount && totalCount > 0;
            const isWriting =
              isLoading &&
              councilProgress.some((p) => p.expert === expert && p.done !== true);

            return (
              <div
                key={expert}
                className={`expert-card border-2 rounded-xl overflow-hidden bg-surface shadow-sm ${palette.border} ${isWriting ? "is-writing" : ""}`}
              >
                {/* Expert card header */}
                <button
                  type="button"
                  aria-expanded={isOpen}
                  className={`w-full flex items-center justify-between px-4 py-3 ${palette.headerBg} transition-[filter] duration-150 hover:brightness-95 active:brightness-90 focus-visible:outline-offset-[-3px]`}
                  onClick={() => onToggleExpert(expert)}
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight
                      aria-hidden="true"
                      className={`disclosure-chevron w-4 h-4 ${palette.accentText}`}
                    />
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${palette.bar}`}
                    />
                    <div className="flex flex-col items-start">
                      <span
                        className={`font-semibold text-sm ${palette.accentText}`}
                      >
                        {expertLabel(expert)}
                      </span>
                      <span className="text-xs text-muted">
                        {isWriting ? "Writing now" : allDone ? "Argument complete" : isOpen ? "Hide argument" : "Show full argument"}
                      </span>
                      <ModelTag
                        entry={latestModelEntry(
                          councilProgress.filter((p) => p.expert === expert),
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-medium font-mono tabular-nums px-2 py-0.5 rounded-md ${palette.badgeBg} ${palette.badgeText}`}
                    >
                      {doneCount}/{totalCount}
                    </span>
                    {allDone ? (
                      <CheckCircle2 className="motion-check w-4 h-4 text-success" />
                    ) : isLoading ? (
                      <Loader2 className="w-4 h-4 text-accent animate-spin" />
                    ) : (
                      <Circle className="w-4 h-4 text-muted" />
                    )}
                  </div>
                </button>

                {/* Collapsed hint — shows step progress and expand prompt */}
                {!isOpen && (
                  <div className="px-4 py-2 border-t border-line">
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {steps.map((step) => (
                        <span
                          key={step.key}
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md border ${
                            step.done
                              ? "bg-success-soft border-success-line text-success"
                              : step.started && isLoading
                                ? "bg-accent-soft border-accent-line text-accent"
                                : "bg-subtle border-line text-muted"
                          }`}
                        >
                          {step.done ? (
                            <CheckCircle2 className="w-3 h-3 text-success" />
                          ) : step.started && isLoading ? (
                            <Loader2 className="w-3 h-3 text-accent animate-spin" />
                          ) : (
                            <Circle className="w-3 h-3 text-muted" />
                          )}
                          {step.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {isOpen && (
                  <div className="motion-fade p-3 space-y-1">
                    {/* Step progress pills */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(() => {
                        const firstNotDoneIdx = steps.findIndex((s) => !s.done);
                        return steps.map((step, idx) => {
                          // Show spinner if: step started but not finished (actively
                          // streaming), or it's the first not-done step while loading
                          const spinning =
                            !step.done &&
                            isLoading &&
                            (step.started || idx === firstNotDoneIdx);
                          return (
                            <span
                              key={step.key}
                              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md border ${
                                step.done
                                  ? "bg-success-soft border-success-line text-success"
                                  : spinning
                                    ? "bg-accent-soft border-accent-line text-accent"
                                    : "bg-subtle border-line text-muted"
                              }`}
                            >
                              {step.done ? (
                                <CheckCircle2 className="w-3 h-3 text-success" />
                              ) : spinning ? (
                                <Loader2 className="w-3 h-3 text-accent animate-spin" />
                              ) : (
                                <Circle className="w-3 h-3 text-muted" />
                              )}
                              {step.label}
                            </span>
                          );
                        });
                      })()}
                    </div>

                    {/* Step content blocks */}
                    <div className="space-y-3">
                      {councilProgress
                        .filter((s) => s.expert === expert)
                        .map((s, idx) => {
                          const style = getStepStyle(s.step);
                          const roundLabel = s.round
                            ? ` · Round ${s.round}`
                            : "";
                          return (
                            <div
                              key={idx}
                              className={`rounded-lg border overflow-hidden ${style.border}`}
                            >
                              <div
                                className={`flex items-center justify-between px-3 py-2 ${style.headerBg}`}
                              >
                                <div className="flex items-center gap-1.5">
                                  <span
                                    className={`text-xs font-semibold ${style.headerText}`}
                                  >
                                    {style.label}
                                    {roundLabel}
                                  </span>
                                </div>
                                {s.done && (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-success bg-success-soft px-1.5 py-0.5 rounded-md border border-success-line">
                                    <CheckCircle2 className="w-3 h-3" /> Done
                                  </span>
                                )}
                              </div>
                              <div
                                className={`p-4 overflow-x-hidden ${style.contentBg}`}
                              >
                                <StreamingMarkdown
                                  text={s.answer || s.feedback || s.revised || ""}
                                  streaming={isLoading && s.done !== true}
                                  components={markdownComponents}
                                  className="prose prose-sm max-w-full break-words leading-relaxed"
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          </div>

          {/* Chaos Agent cards — one per round */}
          {chaosProgress.map((chaosRound) => {
            const isOpen =
              expandedExperts[`chaos-${chaosRound.round}`] === true;
            return (
              <div
                key={`chaos-${chaosRound.round}`}
                className="border-2 border-danger-line rounded-lg p-3 bg-danger-soft"
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  className="w-full flex items-start justify-between rounded-md"
                  onClick={() => onToggleExpert(`chaos-${chaosRound.round}`)}
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight aria-hidden="true" className="disclosure-chevron w-4 h-4 text-danger" />
                    <div className="flex flex-col items-start">
                      <span className="font-semibold text-xs text-danger">
                        Chaos Agent Attack — Round {chaosRound.round}
                      </span>
                      <span className="text-xs text-danger italic">
                        {isOpen
                          ? "Tap to collapse"
                          : "Tap to expand chaos attack details"}
                      </span>
                      <ModelTag entry={latestModelEntry([chaosRound])} />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-danger bg-danger-soft px-2 py-0.5 rounded-md">
                    Adversarial
                  </span>
                </button>
                {isOpen && (
                  <div className="motion-fade mt-3 rounded-lg bg-surface p-4 border border-danger-line overflow-x-hidden">
                    <StreamingMarkdown
                      text={chaosRound.chaos || ""}
                      streaming={isLoading && chaosRound.done !== true}
                      components={markdownComponents}
                      className="prose prose-sm max-w-full break-words leading-relaxed space-y-3"
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Final answer placeholder while waiting */}
          {!councilFinal && isLoading && councilProgress.length > 0 && (
            <div className="border border-dashed border-control rounded-lg px-4 py-3 mt-4">
              <div className="text-sm font-semibold text-content">
                Decision matrix and trade-off analysis
              </div>
              <div className="text-xs text-muted mt-1">
                The facilitator writes this once every expert has revised their position.
              </div>
            </div>
          )}

          {/* Final answer — streams in incrementally */}
          {councilFinal && (
            <div className="border-2 border-accent-line rounded-lg p-4 bg-accent-soft mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-accent">
                  Decision matrix and trade-off analysis
                </div>
                {facilitatorDone && (
                  <CopyButton label="Copy synthesis" getText={() => councilFinal} />
                )}
                {!facilitatorDone && (
                  <div className="flex space-x-1 items-center text-xs text-accent">
                    <span className="typing-dot" aria-hidden="true" />
                    <span className="typing-dot" aria-hidden="true" />
                    <span className="typing-dot" aria-hidden="true" />
                    <span className="ml-1">writing…</span>
                  </div>
                )}
              </div>
              {verdict && (
                <div className="mb-3 rounded-lg bg-surface p-4 border border-line">
                  <h3 className="mb-2 text-label font-medium text-muted">Panel verdict</h3>
                  <VerdictMeter verdict={verdict} />
                </div>
              )}
              <div className="whitespace-pre-wrap text-sm">
                <div className="rounded-lg bg-surface p-4 border border-line overflow-x-hidden">
                  <StreamingMarkdown
                    text={councilFinal}
                    streaming={!facilitatorDone && isLoading}
                    components={markdownComponents}
                    className="prose prose-lg max-w-full break-words leading-relaxed space-y-4"
                  />
                </div>
              </div>
              {facilitatorDone && <SourcesDisclosure sources={visibleSources(sources)} className="mt-3" />}
            </div>
          )}
        </>
      )}
    </div>
    </div>
  );
};

export default DebateProgress;
